"use client";

import Link from "next/link";
import { useEffect, useRef } from "react";
import { useTranslation } from "@/lib/i18n/context";
import { HiwMock } from "./HowItWorksMobileMocks";

const COPY = {
  intro: {
    kicker: { en: "How it works", sl: "Kako deluje" },
    h2A: { en: "One cycle. ", sl: "En cikel. " },
    h2Em: { en: "Under 5 min", sl: "Manj kot 5 min" },
    h2B: { en: " a day.", sl: " na dan." },
    lead: {
      en: "Plan in the morning, focus through the day, and let the journal write itself by night. Keep scrolling to walk through one full day.",
      sl: "Zjutraj načrtuj, čez dan se osredotoči, zvečer pa naj se dnevnik napiše sam. Drsaj naprej skozi en cel dan.",
    },
    scrollcue: { en: "Scroll to move through the day", sl: "Drsaj skozi dan" },
  },
  closerA: { en: "A day that quietly ", sl: "Dan, ki te tiho " },
  closerEm: { en: "pulls toward what matters.", sl: "vleče k bistvenemu." },
};

const STEPS = [
  {
    t: "6:30",
    label: { en: "Plan", sl: "Načrt" },
    meta: { en: "01 / Daily Plan", sl: "01 / Dnevni načrt" },
    h: {
      en: "From a blank page to total alignment.",
      sl: "Od prazne strani do popolne uskladitve.",
    },
    desc: {
      en: "AI reads your long-term goals, pending tasks from your tasks app and your calendars, then drafts a balanced day — so small to-dos never bury the big milestones.",
      sl: "AI prebere tvoje dolgoročne cilje, odprta opravila in koledarje, nato sestavi uravnotežen dan — da drobna opravila nikoli ne pokopljejo velikih mejnikov.",
    },
    opt: false,
  },
  {
    t: "9:00",
    label: { en: "Flow", sl: "Tok" },
    meta: { en: "02 / Daily Flow", sl: "02 / Dnevni tok" },
    h: {
      en: "Your quiet hub for real-time focus.",
      sl: "Tvoje mirno središče za sprotni fokus.",
    },
    desc: {
      en: "Close tasks, add new ones on the fly, capture notes, or ask the side assistant about past patterns — all in one tab.",
      sl: "Zaključuj opravila, sproti dodajaj nova, zapisuj misli ali vprašaj AI pomočnika o preteklih vzorcih — vse v enem zavihku.",
    },
    opt: false,
  },
  {
    t: "18:30",
    label: { en: "Journal", sl: "Dnevnik" },
    meta: { en: "03 / Daily Journal", sl: "03 / Dnevnik" },
    h: {
      en: "Turn daily effort into a lasting legacy.",
      sl: "Spremeni vsakodnevni trud v trajno zapuščino.",
    },
    desc: {
      en: "Journail blends your completed tasks, notes and highlights into a finished journal entry. Your day closes; your growth is saved.",
      sl: "Journail tvoja zaključena opravila, zapiske in poudarke zlije v dokončan dnevniški vnos. Dan se zaključi; tvoja rast je shranjena.",
    },
    opt: false,
  },
  {
    t: "22:00",
    label: { en: "Preview", sl: "Predogled" },
    meta: { en: "04 / Optional", sl: "04 / Neobvezno" },
    h: {
      en: "Offload your mind before sleep.",
      sl: "Razbremeni misli pred spanjem.",
    },
    desc: {
      en: "Sketch tomorrow's key focus in a few words, close the laptop, and rest deeply knowing everything is captured.",
      sl: "V nekaj besedah skiciraj jutrišnji ključni fokus, zapri prenosnik in mirno počivaj, saj je vse zajeto.",
    },
    opt: true,
  },
];

const STEPS_LEN = 4;

function holdMap(rawPos: number, N: number) {
  const k = Math.floor(rawPos);
  if (k >= N - 1) return N - 1;
  const f = rawPos - k;
  const holdStart = k === 0 ? 0.58 : 0.3;
  let t = (f - holdStart) / 0.4;
  t = Math.min(1, Math.max(0, t));
  t = t * t * (3 - 2 * t);
  return k + t;
}

export function HowItWorksMobile() {
  const { t, lang } = useTranslation();
  const en = lang === "en";

  const spacerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLDivElement>(null);
  const counterRef = useRef<HTMLDivElement>(null);
  const nodesRef = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    const pad = (n: number) => (n < 10 ? "0" + n : "" + n);
    let ticking = false;
    let lastActive = -1;

    // Scroll-driven (rAF-coalesced) instead of a permanent rAF loop — keeps the
    // animation smooth while doing zero work when the user isn't scrolling, which
    // matters for battery on the mobile-only surface this renders on.
    const apply = () => {
      ticking = false;
      const spacer = spacerRef.current;
      const track = trackRef.current;
      const fill = fillRef.current;
      if (!spacer || !track || !fill) return;

      const rect = spacer.getBoundingClientRect();
      const denom = rect.height - window.innerHeight;
      let progress = denom > 0 ? -rect.top / denom : 0;
      progress = Math.min(1, Math.max(0, progress));

      const N = STEPS_LEN;
      const pos = holdMap(progress * (N - 1), N);

      track.style.transform = `translate3d(${-pos * track.clientWidth}px,0,0)`;
      fill.style.width = `${(pos / (N - 1)) * 75}%`;

      const active = Math.round(pos);
      if (active !== lastActive) {
        lastActive = active;
        nodesRef.current.forEach((node, i) => {
          if (!node) return;
          node.classList.toggle("on", i === active);
          node.classList.toggle("done", i < active);
        });
        if (counterRef.current) {
          counterRef.current.textContent = `${pad(active + 1)} / 0${N}`;
        }
      }
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(apply);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    apply(); // initial paint

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <section className="hiw lg:hidden">
      <style jsx global>{`
        .hiw {
          max-width: 430px;
          margin: 0 auto;
        }
        .hiw .intro {
          position: relative;
          z-index: 1;
          padding: 64px 24px 40px;
        }
        .hiw .kicker {
          font-family: var(--f-mono, "Geist Mono", monospace);
          font-size: 11px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--terracotta);
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 14px;
        }
        .hiw .kicker::before {
          content: "//";
          opacity: 0.5;
        }
        .hiw .h2 {
          font-family: var(--f-display, "Fraunces", serif);
          font-weight: 360;
          font-size: 34px;
          line-height: 1.04;
          letter-spacing: -0.022em;
          margin: 0 0 14px;
          font-variation-settings: "opsz" 120, "SOFT" 40;
        }
        .hiw .h2 em {
          font-style: italic;
          color: var(--terracotta);
          font-variation-settings: "opsz" 120, "SOFT" 100;
        }
        .hiw .lead {
          font-size: 15px;
          line-height: 1.5;
          color: var(--ink-soft);
          margin: 0 0 26px;
          text-wrap: pretty;
          max-width: 34ch;
        }
        .hiw .scrollcue {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          font-family: var(--f-mono);
          font-size: 10.5px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--ink-faint);
        }
        .hiw .scrollcue .arr {
          display: inline-flex;
          gap: 2px;
          color: var(--terracotta);
        }
        .hiw .scrollcue .arr span {
          animation: hiwnudge 1.6s infinite ease-in-out;
        }
        .hiw .scrollcue .arr span:nth-child(2) {
          animation-delay: 0.15s;
        }
        .hiw .scrollcue .arr span:nth-child(3) {
          animation-delay: 0.3s;
        }
        @keyframes hiwnudge {
          0%,
          100% {
            opacity: 0.3;
            transform: translateX(0);
          }
          50% {
            opacity: 1;
            transform: translateX(2px);
          }
        }
        .hiw .spacer {
          position: relative;
          z-index: 1;
        }
        .hiw .pin {
          position: sticky;
          top: 0;
          height: 100vh;
          height: 100dvh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .hiw .pin-head {
          flex: none;
          /* Top padding clears the 72px sticky site header so the progress
             rail isn't tucked under the menu. */
          padding: 88px 24px 16px;
        }
        .hiw .pin-head-top {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          margin-bottom: 16px;
        }
        .hiw .pin-head-top .kk {
          font-family: var(--f-mono);
          font-size: 10px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--terracotta);
        }
        .hiw .pin-head-top .kk::before {
          content: "// ";
          opacity: 0.5;
        }
        .hiw .pin-head-top .ct {
          font-family: var(--f-mono);
          font-size: 10px;
          letter-spacing: 0.06em;
          color: var(--ink-faint);
        }
        .hiw .track {
          position: relative;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
        }
        .hiw .track-line {
          position: absolute;
          left: 12.5%;
          right: 12.5%;
          top: 15px;
          height: 1.5px;
          background: repeating-linear-gradient(
            90deg,
            var(--rule) 0 3px,
            transparent 3px 7px
          );
        }
        .hiw .track-fill {
          position: absolute;
          left: 12.5%;
          top: 15px;
          height: 1.5px;
          background: var(--terracotta);
          width: 0;
        }
        .hiw .node {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }
        .hiw .node-dot {
          position: relative;
          width: 31px;
          height: 31px;
          border-radius: 50%;
          background: var(--paper);
          border: 1.5px solid var(--rule);
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: var(--f-mono);
          font-size: 11px;
          color: var(--ink-faint);
          transition: border-color 0.3s, color 0.3s, background 0.3s,
            transform 0.3s;
        }
        .hiw .node[data-opt="1"] .node-dot {
          border-style: dashed;
        }
        .hiw .node.done .node-dot {
          border-color: var(--terracotta);
          color: var(--terracotta);
        }
        .hiw .node.on .node-dot {
          border-color: var(--terracotta);
          background: var(--terracotta);
          color: #fff6ee;
          transform: scale(1.08);
          box-shadow: 0 4px 12px rgba(184, 83, 26, 0.28);
        }
        .hiw .node-time {
          font-family: var(--f-mono);
          font-size: 9.5px;
          color: var(--ink-faint);
          transition: color 0.3s;
        }
        .hiw .node-label {
          font-size: 10.5px;
          font-weight: 500;
          color: var(--ink-faint);
          transition: color 0.3s;
          white-space: nowrap;
        }
        .hiw .node.on .node-time {
          color: var(--ink);
        }
        .hiw .node.on .node-label {
          color: var(--terracotta);
        }
        .hiw .stage {
          flex: 1 1 auto;
          position: relative;
          overflow: hidden;
          min-height: 0;
        }
        .hiw .h-track {
          display: flex;
          height: 100%;
          will-change: transform;
        }
        .hiw .panel {
          flex: 0 0 100%;
          height: 100%;
          padding: 14px 24px calc(104px + env(safe-area-inset-bottom));
          display: flex;
          flex-direction: column;
          /* Top-aligned so every step's meta + heading sit at the SAME vertical
             position under the track — centering made short steps (Journal /
             Preview) float with a big empty gap above. Bottom padding clears the
             site's fixed mobile "Start free trial" bar so descriptions stay visible. */
          justify-content: flex-start;
        }
        .hiw .p-meta {
          font-family: var(--f-mono);
          font-size: 10px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--terracotta);
          margin-bottom: 12px;
        }
        .hiw .p-h {
          font-family: var(--f-display);
          font-style: italic;
          font-weight: 400;
          font-size: 26px;
          line-height: 1.1;
          margin: 0 0 18px;
          font-variation-settings: "opsz" 72, "SOFT" 80;
          max-width: 15ch;
        }
        .hiw .p-desc {
          font-size: 14px;
          line-height: 1.5;
          color: var(--ink-soft);
          margin: 16px 0 0;
          max-width: 34ch;
        }
        .hiw .mk {
          background: var(--paper);
          border: 1px solid var(--hair);
          border-radius: 14px;
          padding: 17px 18px;
          box-shadow: 0 10px 30px rgba(26, 24, 21, 0.08);
        }
        .hiw .mk-cap {
          font-family: var(--f-mono);
          font-size: 9px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--ink-faint);
          margin-bottom: 12px;
          display: flex;
          justify-content: space-between;
        }
        .hiw .mk-title {
          font-family: var(--f-display);
          font-size: 17px;
          font-weight: 420;
          margin: 0 0 12px;
        }
        .hiw .mk-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 0;
          border-top: 1px solid var(--hair);
          font-size: 14px;
          color: var(--ink);
        }
        .hiw .mk-row:first-of-type {
          border-top: 0;
        }
        .hiw .mk-row .box {
          width: 17px;
          height: 17px;
          border-radius: 5px;
          border: 1.5px solid var(--rule);
          flex: none;
        }
        .hiw .mk-row .box.done {
          background: var(--terracotta);
          border-color: var(--terracotta);
          position: relative;
        }
        .hiw .mk-row .box.done::after {
          content: "";
          position: absolute;
          left: 5px;
          top: 2px;
          width: 4.5px;
          height: 8.5px;
          border: solid #fff;
          border-width: 0 1.8px 1.8px 0;
          transform: rotate(40deg);
        }
        .hiw .mk-row .nm {
          flex: 1;
        }
        .hiw .mk-row .nm.struck {
          color: var(--ink-faint);
          text-decoration: line-through;
          text-decoration-color: var(--ink-soft);
          text-decoration-thickness: 2px;
        }
        .hiw .mk-row .tm {
          font-family: var(--f-mono);
          font-size: 11px;
          color: var(--ink-faint);
        }
        .hiw .mk-prose {
          font-size: 14px;
          line-height: 1.55;
          color: var(--ink-soft);
          margin: 0;
        }
        .hiw .mk-prose .lead2 {
          color: var(--ink);
          font-weight: 500;
        }
        .hiw .mk-num {
          display: flex;
          gap: 12px;
          align-items: baseline;
          padding: 9px 0;
          font-size: 14px;
        }
        .hiw .mk-num .n {
          font-family: var(--f-mono);
          font-size: 11px;
          color: var(--terracotta);
        }
        .hiw .mk-hint {
          font-size: 11.5px;
          line-height: 1.4;
          color: var(--ink-faint);
          margin: -4px 0 12px;
        }
        .hiw .mk-row .src {
          font-family: var(--f-mono);
          font-size: 8px;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          padding: 2.5px 5px;
          border-radius: 4px;
          white-space: nowrap;
          flex: none;
        }
        .hiw .mk-row .src.goal {
          color: var(--terracotta);
          background: var(--terra-soft);
        }
        .hiw .mk-row .src.cal {
          color: var(--ink-soft);
          background: #1a18150f;
        }
        .hiw .mk-row .src.task {
          color: var(--moss);
          background: #2a4a3b14;
        }
        .hiw .mk-add {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 11px 0 2px;
          border-top: 1px solid var(--hair);
          font-size: 13px;
          color: var(--ink-faint);
        }
        .hiw .mk-add .plus {
          width: 17px;
          height: 17px;
          border-radius: 5px;
          border: 1.5px dashed var(--rule);
          display: flex;
          align-items: center;
          justify-content: center;
          flex: none;
          font-size: 13px;
          line-height: 1;
        }
        .hiw .mk-note {
          display: flex;
          gap: 8px;
          align-items: baseline;
          margin: -1px 0 5px 29px;
          padding: 7px 10px 8px;
          background: color-mix(in oklab, var(--paper) 90%, var(--ink));
          border: 1px solid var(--hair);
          border-radius: 8px;
        }
        .hiw .mk-note .nt {
          font-family: var(--f-mono);
          font-size: 7.5px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--terracotta);
          flex: none;
        }
        .hiw .mk-note .nx {
          font-size: 11.5px;
          line-height: 1.42;
          color: var(--ink-soft);
        }
        .hiw .closer {
          position: relative;
          z-index: 1;
          padding: 56px 24px 80px;
          text-align: left;
        }
        .hiw .closer p {
          font-family: var(--f-display);
          font-style: italic;
          font-size: 22px;
          line-height: 1.25;
          color: var(--ink);
          margin: 0;
          font-variation-settings: "opsz" 72, "SOFT" 70;
          max-width: 18ch;
        }
        .hiw .closer p span {
          color: var(--terracotta);
        }
        @media (prefers-reduced-motion: reduce) {
          .hiw .scrollcue .arr span {
            animation: none;
          }
        }
      `}</style>

      <div className="intro">
        <div className="kicker">{COPY.intro.kicker[en ? "en" : "sl"]}</div>
        <h2 className="h2">
          {COPY.intro.h2A[en ? "en" : "sl"]}
          <em>{COPY.intro.h2Em[en ? "en" : "sl"]}</em>
          {COPY.intro.h2B[en ? "en" : "sl"]}
        </h2>
        <p className="lead">{COPY.intro.lead[en ? "en" : "sl"]}</p>
        <div className="scrollcue">
          {COPY.intro.scrollcue[en ? "en" : "sl"]}
          <span className="arr">
            <span>›</span>
            <span>›</span>
            <span>›</span>
          </span>
        </div>
      </div>

      <div
        className="spacer"
        ref={spacerRef}
        style={{ height: "calc(4 * 128vh)" }}
      >
        <div className="pin">
          <div className="pin-head">
            <div className="pin-head-top">
              <span className="kk">{COPY.intro.kicker[en ? "en" : "sl"]}</span>
              <span className="ct" ref={counterRef}>
                01 / 04
              </span>
            </div>
            <div className="track">
              <div className="track-line" />
              <div className="track-fill" ref={fillRef} />
              {STEPS.map((step, n) => (
                <div
                  key={n}
                  className={`node${n === 0 ? " on" : ""}`}
                  data-opt={step.opt ? "1" : undefined}
                  ref={(el) => {
                    nodesRef.current[n] = el;
                  }}
                >
                  <div className="node-dot">{n + 1}</div>
                  <div className="node-time">{step.t}</div>
                  <div className="node-label">{step.label[en ? "en" : "sl"]}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="stage">
            <div className="h-track" ref={trackRef}>
              {STEPS.map((step, n) => (
                <div className="panel" key={n}>
                  <div className="p-meta">{step.meta[en ? "en" : "sl"]}</div>
                  <h3 className="p-h">{step.h[en ? "en" : "sl"]}</h3>
                  <HiwMock i={n} lang={lang} />
                  <p className="p-desc">{step.desc[en ? "en" : "sl"]}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="closer">
        <p>
          {COPY.closerA[en ? "en" : "sl"]}
          <span>{COPY.closerEm[en ? "en" : "sl"]}</span>
        </p>
      </div>

      {/* Explore link — centered at the bottom of the section */}
      <div className="flex justify-center pb-16 lg:pb-20">
        <Link
          href="/product/daily-rhythm"
          className="inline-flex items-center font-mono text-[12px] tracking-[0.16em] uppercase text-[var(--terracotta)] underline-offset-4 hover:underline"
        >
          {`${t.landing.cover.showcase.learnMore} →`}
        </Link>
      </div>
    </section>
  );
}
